// SNIGJ - Dashboard Page with Analytics
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { t } from '../lib/i18n';
import { dashboardAPI } from '../lib/api';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  EvolutionChart, 
  StatusDistributionChart, 
  TypeDistributionChart, 
  ComparisonChart,
  MultiLineChart
} from '../components/charts/StatsCharts';
import SendNotificationModal from '../components/admin/SendNotificationModal';
import { 
  FileText, 
  Users, 
  CreditCard, 
  AlertTriangle,
  Plus,
  ArrowRight,
  Activity,
  TrendingUp,
  Clock,
  Send,
  BarChart3,
  PieChart,
  LineChart,
  Calendar,
  Bell,
  DollarSign,
  Wallet,
  Shield,
  UserCheck,
  ArrowLeftRight,
  Unlock,
  Timer
} from 'lucide-react';

const DashboardPage = () => {
  const { user, language } = useAuth();
  const { displayAmount, preferredCurrency, getCurrencyInfo } = useCurrency();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [citizenData, setCitizenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showArrestModal, setShowArrestModal] = useState(false);
  const [policeStats, setPoliceStats] = useState(null);

  // Check if user is citizen
  const isCitizen = user?.role === 'CITIZEN';
  
  // Check if user is police/OPJ
  const isPolice = ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL', 'COMMISSAIRE_DIVISIONNAIRE', 'COMMISSAIRE_GENERAL'].includes(user?.role);
  
  // Module access check
  const isAdmin = ['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'MINISTRE_JUSTICE', 'PRESIDENT', 'PREMIER_MINISTRE', 'SUPER_ADMIN'].includes(user?.role);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const userModules = user?.module_access || [];
  const hasModuleConfig = userModules.length > 0;
  const hasAccess = (moduleCode) => {
    if (!moduleCode) return true;
    if (isSuperAdmin || isAdmin) return true;
    if (!hasModuleConfig) return true;
    return userModules.includes(moduleCode);
  };

  useEffect(() => {
    if (isCitizen) {
      loadCitizenData();
    } else {
      loadStats();
      loadAnalytics();
      loadPoliceStats();
    }
  }, []);

  useEffect(() => {
    if (!isCitizen) {
      loadAnalytics();
    }
  }, [period]);

  const loadCitizenData = async () => {
    try {
      const [profileRes, detentionRes] = await Promise.all([
        api.get('/citizen/profile'),
        api.get('/citizen/detention-history').catch(() => ({ data: { detentions: [], detention_count: 0 } }))
      ]);
      setCitizenData({
        profile: profileRes.data,
        detentions: detentionRes.data
      });
    } catch (error) {
      console.error('Failed to load citizen data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/dashboard/analytics', { params: { period } });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const loadPoliceStats = async () => {
    try {
      const response = await api.get('/dashboard/police-stats');
      setPoliceStats(response.data);
    } catch (error) {
      console.error('Failed to load police stats:', error);
    }
  };

  // Citizen-specific stat cards - with currency conversion
  const pendingFeesAmount = citizenData?.profile?.summary?.pending_fees_amount || 0;
  const citizenStatCards = [
    {
      title: t(language, 'dashboard.totalCases'),
      value: citizenData?.profile?.summary?.total_cases || 0,
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      link: '/citizen-portal?tab=cases',
      clickable: true
    },
    {
      title: language === 'fr' ? 'Arrestations' : 'Arrests',
      value: citizenData?.detentions?.detention_count || 0,
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      link: null,
      clickable: true,
      onClick: () => setShowArrestModal(true)
    },
    {
      title: t(language, 'dashboard.pendingPayments'),
      value: displayAmount(pendingFeesAmount, 'USD'),
      icon: CreditCard,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      link: '/citizen-portal?tab=fees',
      clickable: true
    },
    {
      title: t(language, 'dashboard.activeAlerts'),
      value: citizenData?.profile?.summary?.unread_notifications || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: 'bg-red-100 dark:bg-red-900/30',
      link: '/citizen-portal?tab=notifications',
      clickable: true
    }
  ];

  // Admin/Staff stat cards - filtered by module access
  const allStatCards = [
    {
      title: t(language, 'dashboard.totalCases'),
      value: stats?.total_cases || 0,
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      link: '/cases',
      module: 'justice_civile'
    },
    {
      title: t(language, 'dashboard.openCases'),
      value: stats?.open_cases || 0,
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-100 dark:bg-green-900/30',
      link: '/cases?status=OPEN',
      module: 'justice_civile'
    },
    {
      title: t(language, 'dashboard.totalPrisoners'),
      value: stats?.total_prisoners || 0,
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      link: '/prisoners',
      module: 'prisons'
    },
    {
      title: t(language, 'dashboard.pendingPayments'),
      value: stats?.pending_payments || 0,
      icon: CreditCard,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      link: '/payments?status=PENDING',
      module: 'finances'
    },
    {
      title: t(language, 'dashboard.activeAlerts'),
      value: stats?.active_alerts || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: 'bg-red-100 dark:bg-red-900/30',
      link: '/border-control',
      module: 'frontieres'
    }
  ];
  const statCards = allStatCards.filter(c => hasAccess(c.module));

  // Citizen quick actions - Boutons pratiques pour citoyens
  const citizenQuickActions = [
    { label: language === 'fr' ? 'Porter plainte' : 'File Complaint', icon: FileText, link: '/complaints?action=new', color: 'text-red-600' },
    { label: language === 'fr' ? 'Signaler un délit' : 'Report Offense', icon: AlertTriangle, link: '/citizen-portal?action=report-offense', color: 'text-orange-600' },
    { label: language === 'fr' ? 'Contacter le Call Center' : 'Contact Call Center', icon: Send, link: '/citizen-portal?action=contact', color: 'text-blue-600' },
    { label: language === 'fr' ? 'Mes démarches' : 'My Submissions', icon: Plus, link: '/citizen/submissions', color: 'text-green-600' }
  ];

  // Admin quick actions - filtered by module access
  const allQuickActions = [
    { label: t(language, 'dashboard.newCase'), icon: FileText, link: '/cases/new', module: 'justice_civile' },
    { label: t(language, 'dashboard.newPrisoner'), icon: Users, link: '/prisoners/new', module: 'prisons' },
    { label: t(language, 'dashboard.newDocument'), icon: Plus, link: '/documents/upload', module: 'documents' }
  ];
  const quickActions = allQuickActions.filter(a => hasAccess(a.module));

  // Police-specific quick actions
  const policeQuickActions = [
    { label: language === 'fr' ? 'Nouveau Dossier' : 'New Case', icon: FileText, link: '/cases/new', color: 'text-blue-600' },
    { label: language === 'fr' ? 'Enregistrer Detenu' : 'Register Inmate', icon: Users, link: '/prisoners/new', color: 'text-red-600' },
    { label: language === 'fr' ? 'Nouvelle Plainte' : 'New Complaint', icon: AlertTriangle, link: '/complaints', color: 'text-orange-600' },
    { label: language === 'fr' ? 'Mes Dossiers' : 'My Cases', icon: Activity, link: '/cases', color: 'text-green-600' },
  ];

  const periodOptions = [
    { value: 'day', label: language === 'fr' ? 'Aujourd\'hui' : 'Today' },
    { value: 'week', label: language === 'fr' ? 'Cette semaine' : 'This Week' },
    { value: 'month', label: language === 'fr' ? 'Ce mois' : 'This Month' },
    { value: 'year', label: language === 'fr' ? 'Cette année' : 'This Year' }
  ];

  // Check if user can send notifications
  const canSendNotifications = ['POLICE_OFFICER', 'JUDICIAL_POLICE_OFFICER', 'MAGISTRATE', 'CLERK', 
    'NATIONAL_ADMIN', 'TECH_SUPPORT', 'SUPER_ADMIN', 'MINISTER', 'PRESIDENT'].includes(user?.role);

  // Get currency info for display
  const currencyInfo = getCurrencyInfo(preferredCurrency);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {t(language, 'dashboard.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Bienvenue' : 'Welcome'}, {user?.name}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Currency Indicator */}
            <Link to="/settings" className="flex items-center gap-1 px-3 py-1 rounded bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors" data-testid="currency-indicator">
              <Wallet className="w-3 h-3" />
              {currencyInfo?.symbol} {preferredCurrency}
            </Link>
            {canSendNotifications && (
              <Button 
                onClick={() => setShowNotificationModal(true)} 
                className="gap-2"
                data-testid="send-notification-btn"
              >
                <Send className="w-4 h-4" />
                {language === 'fr' ? 'Envoyer convocation' : 'Send Summons'}
              </Button>
            )}
            <span className={`px-3 py-1 rounded text-xs font-medium uppercase tracking-wider classification-${user?.clearance_level?.toLowerCase()}`}>
              {t(language, `classifications.${user?.clearance_level}`)}
            </span>
            <span className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs font-medium">
              {t(language, `roles.${user?.role}`)}
            </span>
          </div>
        </div>

        {/* Stats Grid - Different for Citizens */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isCitizen ? 'lg:grid-cols-4' : statCards.length <= 3 ? 'lg:grid-cols-3' : statCards.length <= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4`}>
          {(isCitizen ? citizenStatCards : statCards).map((card, index) => {
            const CardWrapper = card.link ? Link : 'div';
            const wrapperProps = card.link ? { to: card.link } : { onClick: card.onClick };
            
            return (
              <CardWrapper 
                key={index} 
                {...wrapperProps}
                data-testid={`stat-card-${index}`}
                className={card.clickable || card.link ? 'cursor-pointer' : ''}
              >
                <Card className="hover-lift h-full">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-sm ${card.bg} flex items-center justify-center mb-4`}>
                      <card.icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                    <div className="stat-card-value">
                      {loading ? (
                        <div className="h-8 w-16 skeleton" />
                      ) : (
                        typeof card.value === 'number' ? card.value.toLocaleString() : card.value
                      )}
                    </div>
                    <div className="stat-card-label">{card.title}</div>
                  </CardContent>
                </Card>
              </CardWrapper>
            );
          })}
        </div>

        {/* ===== POLICE / OPJ DASHBOARD SECTION ===== */}
        {!isCitizen && policeStats && (
          <div className="space-y-6" data-testid="police-dashboard-section">
            {/* Police Stats Cards - Row 1: Core */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                {policeStats.is_admin_view
                  ? (language === 'fr' ? 'Vue Globale - Activite' : 'Global View - Activity')
                  : (language === 'fr' ? 'Mon Activite' : 'My Activity')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Link to="/cases" data-testid="police-stat-cases">
                  <Card className="hover-lift cursor-pointer border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold">{policeStats.cases?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Dossiers' : 'Cases'}</p>
                      <p className="text-xs text-blue-600 mt-1">{policeStats.cases?.open || 0} {language === 'fr' ? 'ouverts' : 'open'}</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/complaints" data-testid="police-stat-complaints">
                  <Card className="hover-lift cursor-pointer border-l-4 border-l-orange-500">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold">{policeStats.complaints?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Plaintes' : 'Complaints'}</p>
                      <p className="text-xs text-orange-600 mt-1">{policeStats.complaints?.pending || 0} {language === 'fr' ? 'en attente' : 'pending'}</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/prisoners" data-testid="police-stat-prisoners">
                  <Card className="hover-lift cursor-pointer border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold">{policeStats.prisoners?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Detenus' : 'Inmates'}</p>
                      <p className="text-xs text-orange-600 mt-1">{policeStats.prisoners?.preventive || 0} {language === 'fr' ? 'preventifs' : 'preventive'}</p>
                    </CardContent>
                  </Card>
                </Link>
                <Card className="border-l-4 border-l-green-500" data-testid="police-stat-closed">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{policeStats.cases?.closed || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Dossiers Clotures' : 'Closed Cases'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-purple-500" data-testid="police-stat-pending">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{policeStats.cases?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'En cours' : 'In Progress'}</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Police Stats Cards - Row 2: Interpellations, Gardes à vue, Transfers, Releases */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                {language === 'fr' ? 'Activite Police / OPJ' : 'Police / OPJ Activity'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="border-l-4 border-l-indigo-500" data-testid="police-stat-interpellations">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <UserCheck className="w-4 h-4 text-indigo-600" />
                    </div>
                    <p className="text-2xl font-bold">{policeStats.interpellations?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Interpellations' : 'Apprehensions'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500" data-testid="police-stat-detentions">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Timer className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold">{policeStats.detentions?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Gardes a vue' : 'Detentions'}</p>
                    <p className="text-xs text-amber-600 mt-1">{policeStats.detentions?.en_cours || 0} {language === 'fr' ? 'en cours' : 'active'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-yellow-500" data-testid="police-stat-prolongations">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                    </div>
                    <p className="text-2xl font-bold">{policeStats.detentions?.prolongees || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Prolongations' : 'Extensions'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-cyan-500" data-testid="police-stat-transfers">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <ArrowLeftRight className="w-4 h-4 text-cyan-600" />
                    </div>
                    <p className="text-2xl font-bold">{policeStats.transfers || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Transferts' : 'Transfers'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500" data-testid="police-stat-releases">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Unlock className="w-4 h-4 text-emerald-600" />
                    </div>
                    <p className="text-2xl font-bold">{policeStats.releases || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Liberations' : 'Releases'}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500" data-testid="police-stat-sentenced">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold">{policeStats.prisoners?.sentenced || 0}</p>
                    <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Condamnes' : 'Sentenced'}</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Police Quick Actions */}
            {isPolice && (
              <div>
                <h2 className="text-lg font-semibold mb-3">{language === 'fr' ? 'Actions Rapides' : 'Quick Actions'}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {policeQuickActions.map((action, i) => (
                    <Link key={i} to={action.link} data-testid={`police-action-${i}`}>
                      <Card className="hover-lift cursor-pointer group">
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                            <action.icon className={`w-5 h-5 ${action.color}`} />
                          </div>
                          <span className="text-sm font-medium">{action.label}</span>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Recent Cases */}
              <Card data-testid="police-recent-cases">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    {language === 'fr' ? 'Derniers Dossiers' : 'Recent Cases'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {policeStats.recent_cases?.length > 0 ? policeStats.recent_cases.map((c, i) => (
                    <Link key={i} to={`/cases/${c.case_id}`} className="block p-2 rounded hover:bg-muted/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.title || c.case_number || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground">{c.case_number}</p>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                          c.status === 'OPEN' ? 'bg-green-100 text-green-700' :
                          c.status === 'CLOSED' ? 'bg-gray-100 text-gray-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{c.status}</span>
                      </div>
                    </Link>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucun dossier' : 'No cases'}</p>
                  )}
                  <Link to="/cases" className="block text-xs text-center text-primary hover:underline pt-2">
                    {language === 'fr' ? 'Voir tous les dossiers' : 'View all cases'} <ArrowRight className="w-3 h-3 inline" />
                  </Link>
                </CardContent>
              </Card>

              {/* Recent Interpellations & Detentions */}
              <Card data-testid="police-recent-interpellations">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    {language === 'fr' ? 'Interpellations & Gardes a vue' : 'Apprehensions & Detentions'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(policeStats.recent_interpellations?.length > 0 || policeStats.recent_detentions?.length > 0) ? (
                    <>
                      {policeStats.recent_interpellations?.map((item, i) => (
                        <div key={`interp-${i}`} className="p-2 rounded bg-indigo-50/50 dark:bg-indigo-950/20">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{item.person_name || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground truncate">{item.motif}</p>
                            </div>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 shrink-0">Interp.</span>
                          </div>
                        </div>
                      ))}
                      {policeStats.recent_detentions?.map((item, i) => (
                        <div key={`det-${i}`} className="p-2 rounded bg-amber-50/50 dark:bg-amber-950/20">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{item.person_name || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground truncate">{item.motif} - {item.duree_heures}h</p>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                              item.status === 'EN_COURS' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                            }`}>{item.status === 'EN_COURS' ? 'GAV' : item.status}</span>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucune interpellation' : 'No apprehensions'}</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Prisoners */}
              <Card data-testid="police-recent-prisoners">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-red-600" />
                    {language === 'fr' ? 'Derniers Detenus' : 'Recent Inmates'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {policeStats.recent_prisoners?.length > 0 ? policeStats.recent_prisoners.map((p, i) => (
                    <Link key={i} to={`/prisoners/${p.prisoner_id}`} className="block p-2 rounded hover:bg-muted/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.last_name} {p.first_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.charges || '-'}</p>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                          p.status === 'PREVENTIVE' ? 'bg-orange-100 text-orange-700' :
                          p.status === 'SENTENCED' ? 'bg-red-100 text-red-700' :
                          p.status === 'RELEASED' ? 'bg-green-100 text-green-700' :
                          p.status === 'TRANSFERRED' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{p.status}</span>
                      </div>
                    </Link>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucun detenu' : 'No inmates'}</p>
                  )}
                  <Link to="/prisoners" className="block text-xs text-center text-primary hover:underline pt-2">
                    {language === 'fr' ? 'Voir tous les detenus' : 'View all inmates'} <ArrowRight className="w-3 h-3 inline" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Arrest History Modal for Citizens */}
        {isCitizen && showArrestModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
              <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-card z-10">
                <CardTitle>{language === 'fr' ? 'Historique des Arrestations/Libérations' : 'Arrest/Release History'}</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowArrestModal(false)}>
                  <AlertTriangle className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {citizenData?.detentions?.detentions?.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-green-500 mb-4" />
                    <p className="text-green-600 font-medium">
                      {language === 'fr' ? 'Aucune arrestation enregistrée' : 'No arrests on record'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {citizenData?.detentions?.detentions?.map((detention, index) => (
                      <div key={index} className="p-4 rounded border">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            detention.type === 'ARREST' ? 'bg-red-100 text-red-800' :
                            detention.type === 'RELEASE' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {detention.type}
                          </span>
                          <span className="text-sm text-muted-foreground">{detention.date}</span>
                        </div>
                        <p className="font-medium">{detention.reason || language === 'fr' ? 'Raison non spécifiée' : 'Reason not specified'}</p>
                        {detention.release_reason && (
                          <p className="text-sm text-green-600 mt-2">
                            <strong>{language === 'fr' ? 'Motif de libération:' : 'Release reason:'}</strong> {detention.release_reason}
                          </p>
                        )}
                        {detention.prison && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {language === 'fr' ? 'Lieu:' : 'Location:'} {detention.prison}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Section - Only for non-citizens */}
        {!isCitizen && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              {language === 'fr' ? 'Analyses & Statistiques' : 'Analytics & Statistics'}
            </CardTitle>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="evolution" className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="evolution" className="gap-2">
                  <LineChart className="w-4 h-4" />
                  {language === 'fr' ? 'Évolution' : 'Evolution'}
                </TabsTrigger>
                <TabsTrigger value="distribution" className="gap-2">
                  <PieChart className="w-4 h-4" />
                  {language === 'fr' ? 'Répartition' : 'Distribution'}
                </TabsTrigger>
                <TabsTrigger value="comparison" className="gap-2">
                  <BarChart3 className="w-4 h-4" />
                  {language === 'fr' ? 'Comparaison' : 'Comparison'}
                </TabsTrigger>
                <TabsTrigger value="summary" className="gap-2">
                  <Activity className="w-4 h-4" />
                  {language === 'fr' ? 'Résumé' : 'Summary'}
                </TabsTrigger>
              </TabsList>

              {/* Evolution Tab */}
              <TabsContent value="evolution">
                {analytics?.evolution_data && (
                  <div className="grid grid-cols-1 gap-6">
                    <MultiLineChart 
                      data={analytics.evolution_data}
                      title={language === 'fr' ? `Évolution (${periodOptions.find(p => p.value === period)?.label})` : `Evolution (${periodOptions.find(p => p.value === period)?.label})`}
                      lines={[
                        { dataKey: 'cases', name: language === 'fr' ? 'Dossiers' : 'Cases' },
                        { dataKey: 'prisoners', name: language === 'fr' ? 'Détenus' : 'Prisoners' },
                        { dataKey: 'payments', name: language === 'fr' ? 'Paiements' : 'Payments' }
                      ]}
                    />
                  </div>
                )}
              </TabsContent>

              {/* Distribution Tab */}
              <TabsContent value="distribution">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {analytics?.cases_by_status && (
                    <StatusDistributionChart 
                      data={analytics.cases_by_status}
                      title={language === 'fr' ? 'Dossiers par statut' : 'Cases by Status'}
                    />
                  )}
                  {analytics?.cases_by_type && (
                    <TypeDistributionChart 
                      data={analytics.cases_by_type}
                      title={language === 'fr' ? 'Dossiers par type' : 'Cases by Type'}
                    />
                  )}
                  {analytics?.prisoners_by_status && (
                    <StatusDistributionChart 
                      data={analytics.prisoners_by_status}
                      title={language === 'fr' ? 'Détenus par statut' : 'Prisoners by Status'}
                    />
                  )}
                </div>
              </TabsContent>

              {/* Comparison Tab */}
              <TabsContent value="comparison">
                {analytics?.comparison_data && (
                  <ComparisonChart 
                    data={analytics.comparison_data}
                    title={language === 'fr' ? 'Comparaison périodes' : 'Period Comparison'}
                    period1Name={language === 'fr' ? 'Période actuelle' : 'Current Period'}
                    period2Name={language === 'fr' ? 'Période précédente' : 'Previous Period'}
                  />
                )}
              </TabsContent>

              {/* Summary Tab */}
              <TabsContent value="summary">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                      <FileText className="w-8 h-8 text-blue-600 mb-2" />
                      <p className="text-2xl font-bold">{analytics?.totals?.total_cases || 0}</p>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total dossiers' : 'Total Cases'}</p>
                      <div className="mt-2 text-xs space-y-1">
                        <p className="text-green-600">{analytics?.totals?.open_cases || 0} {language === 'fr' ? 'ouverts' : 'open'}</p>
                        <p className="text-yellow-600">{analytics?.totals?.in_progress_cases || 0} {language === 'fr' ? 'en cours' : 'in progress'}</p>
                        <p className="text-gray-600">{analytics?.totals?.closed_cases || 0} {language === 'fr' ? 'fermés' : 'closed'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                    <CardContent className="p-4">
                      <Users className="w-8 h-8 text-purple-600 mb-2" />
                      <p className="text-2xl font-bold">{analytics?.totals?.total_prisoners || 0}</p>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total détenus' : 'Total Prisoners'}</p>
                      <div className="mt-2 text-xs space-y-1">
                        <p className="text-yellow-600">{analytics?.totals?.preventive_prisoners || 0} {language === 'fr' ? 'préventifs' : 'preventive'}</p>
                        <p className="text-red-600">{analytics?.totals?.sentenced_prisoners || 0} {language === 'fr' ? 'condamnés' : 'sentenced'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CardContent className="p-4">
                      <CreditCard className="w-8 h-8 text-green-600 mb-2" />
                      <p className="text-2xl font-bold">{analytics?.totals?.total_payments || 0}</p>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total paiements' : 'Total Payments'}</p>
                      <div className="mt-2 text-xs space-y-1">
                        <p className="text-yellow-600">{analytics?.totals?.pending_payments || 0} {language === 'fr' ? 'en attente' : 'pending'}</p>
                        <p className="text-green-600">{analytics?.totals?.completed_payments || 0} {language === 'fr' ? 'complétés' : 'completed'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                    <CardContent className="p-4">
                      <Bell className="w-8 h-8 text-red-600 mb-2" />
                      <p className="text-2xl font-bold">{analytics?.totals?.total_notifications || 0}</p>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Notifications' : 'Notifications'}</p>
                      <div className="mt-2 text-xs space-y-1">
                        <p className="text-red-600">{analytics?.totals?.active_alerts || 0} {language === 'fr' ? 'alertes actives' : 'active alerts'}</p>
                        <p className="text-yellow-600">{analytics?.totals?.pending_fees || 0} {language === 'fr' ? 'frais en attente' : 'pending fees'}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">
                {t(language, 'dashboard.quickActions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(isCitizen ? citizenQuickActions : quickActions).map((action, index) => (
                <Link key={index} to={action.link}>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3"
                    data-testid={`quick-action-${index}`}
                  >
                    <action.icon className="w-4 h-4" />
                    {action.label}
                  </Button>
                </Link>
              ))}
              {canSendNotifications && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 text-primary border-primary/50"
                  onClick={() => setShowNotificationModal(true)}
                >
                  <Send className="w-4 h-4" />
                  {language === 'fr' ? 'Envoyer convocation/amende' : 'Send Summons/Fine'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">
                {t(language, 'dashboard.recentActivity')}
              </CardTitle>
              <Link to="/audit-logs">
                <Button variant="ghost" size="sm" className="gap-2">
                  {t(language, 'dashboard.viewAll')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-8 h-8 skeleton rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 skeleton" />
                        <div className="h-3 w-1/2 skeleton" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : stats?.recent_activity?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_activity.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex items-start gap-4 text-sm">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <Activity className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {activity.action} - {activity.resource_type}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {activity.user_email}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(activity.timestamp).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t(language, 'common.noData')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* System Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">
              {language === 'fr' ? "État du système" : "System Status"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <div>
                  <p className="text-sm font-medium">API</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Opérationnel' : 'Operational'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <div>
                  <p className="text-sm font-medium">Base de données</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Opérationnel' : 'Operational'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <div>
                  <p className="text-sm font-medium">{language === 'fr' ? 'Stockage' : 'Storage'}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Opérationnel' : 'Operational'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <div>
                  <p className="text-sm font-medium">{language === 'fr' ? 'Authentification' : 'Auth'}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Opérationnel' : 'Operational'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send Notification Modal */}
      <SendNotificationModal 
        open={showNotificationModal} 
        onOpenChange={setShowNotificationModal}
      />
    </DashboardLayout>
  );
};

export default DashboardPage;
